if (!customElements.get("product-modal")) {
  customElements.define(
    "product-modal",
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.clickPosition = null;
        this.zoomState = new Map(); // Track zoom state per image
        this.isDragging = false;
        this.didDrag = false; // Track if actual drag movement occurred
        this.dragStart = { x: 0, y: 0 };
        this.scrollStart = { x: 0, y: 0 };
        this.currentWrapper = null;
        this.currentDragImg = null; // Track the image being dragged for cursor reset
        // Touch pinch-to-zoom state
        this.initialPinchDistance = null;
        this.initialPinchZoom = 1;
        this.currentTouchImg = null;
        this.currentTouchWrapper = null;
        this.pinchCenter = { x: 0, y: 0 };
        // Threshold for closing lightbox when zooming out (0.9 = below normal size on mobile where CSS is 100vw)
        this.closeOnZoomOutThreshold = 0.9;
        this.isClosingFromZoomOut = false;
        // Flag to indicate we're handling an external pinch gesture (from gallery)
        this.isExternalPinchActive = false;
      }

      // Find the active image in the modal. The active [data-media-id] element
      // may itself BE an <img> (for photos) or CONTAIN an <img> (for video/deferred-media).
      findActiveImg() {
        const activeMedia = this.querySelector("[data-media-id].active");
        if (activeMedia) {
          if (activeMedia.tagName === "IMG") return activeMedia;
          const img = activeMedia.querySelector("img");
          if (img) return img;
        }
        // Fallback: first img in modal content
        const wrapper = this.querySelector(".product-media-modal__content");
        return wrapper ? wrapper.querySelector("img") : null;
      }

      // Hand the deferred lightbox sources to the browser. They are kept in
      // data-src so they cost nothing during page load; once the lightbox is on
      // screen native lazy loading fetches only the slides actually in view.
      revealDeferredImages() {
        this.querySelectorAll("img[data-src]").forEach((img) => {
          img.src = img.dataset.src;
          delete img.dataset.src;
        });
      }

      // Copy the gallery image's cached src to the modal image for instant display
      preloadActiveImageFromGallery() {
        const img = this.findActiveImg();
        if (!img || !this.openedBy) return;

        const fullSrc = img.dataset.fullSrc
          ? new URL(img.dataset.fullSrc, document.baseURI).href
          : null;

        // Force eager loading
        img.loading = "eager";

        if (
          fullSrc &&
          img.complete &&
          img.naturalWidth > 0 &&
          (img.currentSrc || img.src) === fullSrc
        ) {
          return;
        }

        // Find the gallery image that's already loaded and copy its src
        const mediaId = this.openedBy.getAttribute("data-media-id");
        if (!mediaId) return;

        const galleryImg = this.openedBy
          .closest("modal-opener")
          ?.querySelector(".product__media img");
        if (galleryImg && galleryImg.currentSrc) {
          img.src = galleryImg.currentSrc;
        }

        if (!fullSrc || fullSrc === img.src) return;

        const fullImage = new Image();
        fullImage.onload = () => {
          if (this.findActiveImg() === img) {
            img.src = fullSrc;
          }
        };
        fullImage.src = fullSrc;
      }

      hide() {
        // Determine which image to scroll back to on the product page
        let visibleMediaId = null;

        const isMobile =
          window.matchMedia("(hover: none)").matches ||
          window.matchMedia("(max-width: 749px)").matches;

        if (isMobile) {
          // On mobile, only one image is shown - use the one that was clicked to open
          if (this.openedBy) {
            visibleMediaId = this.openedBy.getAttribute("data-media-id");
          }
        } else {
          // On desktop, detect which image is currently visible by scroll position
          const container = this.querySelector('[role="document"]');
          const mediaItems = this.querySelectorAll("[data-media-id]");

          if (container && mediaItems.length > 0) {
            const scrollTop = container.scrollTop;
            const viewportCenter = scrollTop + container.clientHeight / 2;

            // Find the media item that's most visible (closest to viewport center)
            let closestItem = null;
            let closestDistance = Infinity;

            mediaItems.forEach((item) => {
              const itemCenter = item.offsetTop + item.offsetHeight / 2;
              const distance = Math.abs(viewportCenter - itemCenter);
              if (distance < closestDistance) {
                closestDistance = distance;
                closestItem = item;
              }
            });

            if (closestItem) {
              visibleMediaId = closestItem.getAttribute("data-media-id");
            }
          }
        }

        // Reset all zoom states and content-ready/pinch-open classes for next open
        this.resetAllZoom();
        const wrapper = this.querySelector(".product-media-modal__content");
        if (wrapper) {
          wrapper.classList.remove("content-ready");
          wrapper.classList.remove("pinch-open");
        }

        // Clear external pinch flag, timestamp, and document listeners
        this.isExternalPinchActive = false;
        this.pinchOpenTimestamp = null;
        this.removeDocumentTouchListeners();

        super.hide();

        // Scroll product page to the image that was visible in the lightbox (desktop only)
        // On mobile, don't scroll - user is already at the image position
        if (visibleMediaId && !isMobile) {
          setTimeout(() => {
            // Find the corresponding image on the product page using media-gallery element
            // This selector works across all locales (aria-label is localized)
            const gallery = document.querySelector("media-gallery");
            if (gallery) {
              const productImage = gallery.querySelector(
                `[data-media-id$="-${visibleMediaId}"]`,
              );
              if (productImage) {
                productImage.scrollIntoView({
                  behavior: "instant",
                  block: "start",
                });
              }
            }
          }, 50);
        }
      }

      show(opener, clickPosition = null) {
        this.clickPosition = clickPosition;
        this.isClosingFromZoomOut = false;

        // Check if we're continuing from an existing pinch gesture
        const continuingPinch =
          clickPosition && clickPosition.initialPinchDistance;

        // Set up pinch state BEFORE showing (so it's ready immediately)
        if (continuingPinch) {
          this.pendingPinchState = {
            initialPinchDistance: clickPosition.initialPinchDistance,
            pinchCenter: clickPosition.pinchCenter || { x: 50, y: 50 },
          };
          // Mark that we're handling an external pinch - modal's own handlers should not interfere
          this.isExternalPinchActive = true;
          // Record timestamp for timing-based fallback (in case flags don't work on real devices)
          this.pinchOpenTimestamp = Date.now();
        } else {
          this.pinchOpenTimestamp = null;
        }

        super.show(opener);
        this.revealDeferredImages();
        // After showActiveMedia: that is what marks the slide .active, and the
        // preload resolves the active slide to paint it from the gallery's
        // cached bitmap.
        this.showActiveMedia();
        this.preloadActiveImageFromGallery();
        this.initZoomHandlers();
        this.initModalThumbnails();

        // For pinch-to-open, show content immediately (no fade-in, no transition) to avoid white flash
        if (continuingPinch) {
          const wrapper = this.querySelector(".product-media-modal__content");
          if (wrapper) {
            // Add pinch-open class for instant visibility (no CSS transition)
            wrapper.classList.add("pinch-open");
            wrapper.classList.add("content-ready");
          }
          // Preload the active modal image from the gallery's cached image
          this.preloadActiveImageFromGallery();
          this.setupContinuedPinch();
          // Add document-level touch listeners to capture touches that might not reach the modal
          this.setupDocumentTouchListeners();
        } else {
          this.applyInitialZoom();
        }
      }

      setupContinuedPinch() {
        if (!this.pendingPinchState) return;

        const wrapper = this.querySelector(".product-media-modal__content");
        const img = this.findActiveImg();

        if (!img) {
          // Image not ready yet - will be handled by handleTouchMove or handleExternalTouchMove
          return;
        }

        // Initialize pinch state so touchmove can continue the gesture
        this.initialPinchDistance = this.pendingPinchState.initialPinchDistance;
        this.initialPinchZoom = 1;
        this.currentTouchImg = img;
        this.currentTouchWrapper =
          wrapper || img.closest(".product-media-modal__content");
        this.pinchCenter = this.pendingPinchState.pinchCenter;

        // Initialize zoom state for this image
        if (!this.zoomState.has(img)) {
          this.zoomState.set(img, { zoom: 1, panX: 0, panY: 0 });
        }

        this.pendingPinchState = null;
      }

      setupDocumentTouchListeners() {
        // Add document-level touch listeners to capture touches during pinch-to-open
        // This helps on iOS where touches might not reach the modal element
        this.documentTouchMoveHandler = (e) => {
          if (!this.hasAttribute("open")) return;
          if (e.touches.length >= 2) {
            // Forward to our touch handler
            this.handleTouchMove(e);
          }
        };
        this.documentTouchEndHandler = (e) => {
          if (!this.hasAttribute("open")) return;
          this.handleTouchEnd(e);
          // Clean up document listeners after gesture ends
          if (e.touches.length === 0) {
            this.removeDocumentTouchListeners();
          }
        };

        document.addEventListener("touchmove", this.documentTouchMoveHandler, {
          passive: false,
        });
        document.addEventListener("touchend", this.documentTouchEndHandler);
      }

      removeDocumentTouchListeners() {
        if (this.documentTouchMoveHandler) {
          document.removeEventListener(
            "touchmove",
            this.documentTouchMoveHandler,
          );
          this.documentTouchMoveHandler = null;
        }
        if (this.documentTouchEndHandler) {
          document.removeEventListener(
            "touchend",
            this.documentTouchEndHandler,
          );
          this.documentTouchEndHandler = null;
        }
      }

      initModalThumbnails() {
        const thumbnailsContainer = this.querySelector(
          ".product__modal-thumbnails",
        );
        if (!thumbnailsContainer) return;

        // Prevent all clicks on thumbnails container from bubbling to modal (which would close it)
        thumbnailsContainer.addEventListener("click", (e) =>
          e.stopPropagation(),
        );
        thumbnailsContainer.addEventListener("pointerup", (e) =>
          e.stopPropagation(),
        );

        const thumbnailItems = thumbnailsContainer.querySelectorAll(
          "[data-modal-media-index]",
        );
        const mediaItems = this.querySelectorAll(
          ".product-media-modal__content > [data-media-id]",
        );
        const container = this.querySelector('[role="document"]');

        // Set initial active thumbnail based on opened media
        this.updateModalActiveThumbnail();

        // Add click handlers to thumbnails
        thumbnailItems.forEach((item) => {
          const button = item.querySelector("button");
          if (!button) return;

          button.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            const index = parseInt(item.dataset.modalMediaIndex, 10);
            const targetMedia = mediaItems[index];
            if (targetMedia && container) {
              targetMedia.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
              this.setModalActiveThumbnail(index);
            }
          });
        });

        // Track scroll to update active thumbnail
        if (container && !container.dataset.thumbnailScrollInitialized) {
          container.dataset.thumbnailScrollInitialized = "true";
          let scrollTimeout;
          container.addEventListener("scroll", () => {
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => {
              this.updateModalActiveThumbnail();
            }, 100);
          });
        }
      }

      updateModalActiveThumbnail() {
        const container = this.querySelector('[role="document"]');
        const mediaItems = this.querySelectorAll(
          ".product-media-modal__content > [data-media-id]",
        );
        if (!container || mediaItems.length === 0) return;

        const scrollTop = container.scrollTop;
        const viewportCenter = scrollTop + container.clientHeight / 2;

        let closestIndex = 0;
        let closestDistance = Infinity;

        mediaItems.forEach((item, index) => {
          const itemCenter = item.offsetTop + item.offsetHeight / 2;
          const distance = Math.abs(viewportCenter - itemCenter);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        this.setModalActiveThumbnail(closestIndex);
      }

      setModalActiveThumbnail(index) {
        const thumbnailItems = this.querySelectorAll(
          ".product__modal-thumbnails [data-modal-media-index]",
        );
        thumbnailItems.forEach((item, i) => {
          item.classList.toggle("is-active", i === index);
        });
      }

      // Calculate the minimum zoom needed so the image fills the viewport completely
      calculateFillZoom(img, wrapper) {
        const imgW = img.offsetWidth || img.naturalWidth || 1;
        const imgH = img.offsetHeight || img.naturalHeight || 1;
        const wrapperW = wrapper.clientWidth || window.innerWidth;
        const wrapperH = wrapper.clientHeight || window.innerHeight;
        return Math.max(wrapperW / imgW, wrapperH / imgH);
      }

      applyInitialZoom() {
        const wrapper = this.querySelector(".product-media-modal__content");
        if (!wrapper) return;

        const img = this.findActiveImg();

        // If no click position or no image, just show content immediately
        if (!this.clickPosition || !img) {
          wrapper.classList.add("content-ready");
          return;
        }

        const isMobile =
          window.matchMedia("(hover: none)").matches ||
          window.matchMedia("(max-width: 749px)").matches;

        // Reset scroll position immediately to prevent "jumping" when reopening
        wrapper.scrollLeft = 0;
        wrapper.scrollTop = 0;

        // Check if this is a pinch-to-open (has initialPinchDistance) or click-to-open
        const isPinchOpen =
          this.clickPosition && this.clickPosition.initialPinchDistance;

        // For click-to-open on mobile, preload the gallery image and apply zoom
        if (isMobile && !isPinchOpen) {
          this.preloadActiveImageFromGallery();
          // Use at least 2x zoom, but increase if needed to fill viewport
          const fillZoom = this.calculateFillZoom(img, wrapper);
          const effectiveZoom = Math.max(2, fillZoom);
          this.applyZoom(img, effectiveZoom);
          this.zoomState.set(img, { zoom: effectiveZoom, panX: 0, panY: 0 });
        }

        // Function to position and reveal the content
        const positionAndReveal = () => {
          requestAnimationFrame(() => {
            const clickX = this.clickPosition.clickX;
            const clickY = this.clickPosition.clickY;

            if (isMobile && !isPinchOpen) {
              // Recalculate zoom with actual loaded dimensions to guarantee coverage
              // Use base width (pre-zoom) for calculation
              const baseWidth =
                parseFloat(img.dataset.baseWidth) || img.offsetWidth;
              const baseHeight = img.naturalHeight
                ? (baseWidth * img.naturalHeight) / img.naturalWidth
                : img.offsetHeight;
              const fillZoom = Math.max(
                wrapper.clientWidth / baseWidth,
                wrapper.clientHeight / baseHeight,
              );
              const effectiveZoom = Math.max(2, fillZoom);
              const state = this.zoomState.get(img);
              if (state) state.zoom = effectiveZoom;
              this.applyZoom(img, effectiveZoom);

              // img.offsetWidth is now the zoomed width (actual layout)
              const scrollX =
                img.offsetWidth * clickX - wrapper.clientWidth / 2;
              const scrollY =
                img.offsetHeight * clickY - wrapper.clientHeight / 2;

              wrapper.scrollLeft = Math.max(0, scrollX);
              wrapper.scrollTop = Math.max(0, scrollY);
            } else if (isMobile) {
              // Pinch-to-open: no zoom applied yet, just position
              const scrollX =
                img.offsetWidth * clickX - wrapper.clientWidth / 2;
              const scrollY =
                img.offsetHeight * clickY - wrapper.clientHeight / 2;

              wrapper.scrollLeft = Math.max(0, scrollX);
              wrapper.scrollTop = Math.max(0, scrollY);
            } else {
              // On desktop, image is at 100vw
              const containerRect = wrapper.getBoundingClientRect();

              const clickedPointX = img.offsetWidth * clickX;
              const clickedPointY = img.offsetHeight * clickY;

              const scrollX = clickedPointX - containerRect.width / 2;
              const scrollY =
                img.offsetTop + clickedPointY - containerRect.height / 2;

              wrapper.scrollLeft = Math.max(0, scrollX);
              wrapper.scrollTop = Math.max(0, scrollY);
            }

            // Reveal content after positioning with a small delay for scroll to apply
            requestAnimationFrame(() => {
              wrapper.classList.add("content-ready");
            });
          });
        };

        // Wait for image to load before calculating dimensions
        if (img.complete && img.naturalHeight !== 0) {
          // Image already loaded
          setTimeout(positionAndReveal, 50);
        } else {
          // Wait for image to load
          const onLoad = () => {
            img.removeEventListener("load", onLoad);
            positionAndReveal();
          };
          img.addEventListener("load", onLoad);

          // Fallback timeout in case load event doesn't fire
          setTimeout(() => {
            img.removeEventListener("load", onLoad);
            positionAndReveal();
          }, 500);
        }
      }

      initZoomHandlers() {
        const container = this.querySelector('[role="document"]');
        if (!container || container.dataset.zoomInitialized) return;

        container.dataset.zoomInitialized = "true";

        // Mouse wheel zoom (works on desktop and some touch devices with trackpads)
        container.addEventListener("wheel", (e) => this.handleWheel(e), {
          passive: false,
        });

        // Mouse drag for panning (desktop)
        container.addEventListener("mousedown", (e) => this.handleMouseDown(e));
        container.addEventListener("mousemove", (e) => this.handleMouseMove(e));
        container.addEventListener("mouseup", () => this.handleMouseUp());
        container.addEventListener("mouseleave", () => this.handleMouseUp());

        // Prevent click and pointerup events when dragging (to avoid closing lightbox)
        // ModalDialog uses pointerup to close media-modal, so we need to intercept it
        container.addEventListener("click", (e) => this.handleClick(e), true);
        container.addEventListener(
          "pointerup",
          (e) => this.handlePointerUp(e),
          true,
        );

        // Touch events for pinch-to-zoom (mobile)
        container.addEventListener(
          "touchstart",
          (e) => this.handleTouchStart(e),
          { passive: false },
        );
        container.addEventListener(
          "touchmove",
          (e) => this.handleTouchMove(e),
          { passive: false },
        );
        container.addEventListener("touchend", (e) => this.handleTouchEnd(e));
      }

      handleWheel(e) {
        // Only zoom on trackpad pinch gesture (ctrlKey is set)
        // Regular wheel scrolling should scroll through images normally
        if (!e.ctrlKey) return;

        const img = e.target.closest("img");
        if (!img) return;

        const wrapper = img.closest(".product-media-modal__content");
        if (!wrapper) return;

        e.preventDefault();

        // Get or initialize zoom state for this image
        let state = this.zoomState.get(img);
        if (!state) {
          state = { zoom: 1, panX: 0, panY: 0 };
          this.zoomState.set(img, state);
        }

        // Trackpad pinch-to-zoom (finer control)
        const delta = -e.deltaY * 0.01;
        const newZoom = Math.max(1, Math.min(4, state.zoom + delta));

        if (newZoom === state.zoom) return;

        // Get mouse position relative to wrapper
        const rect = wrapper.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        // Calculate zoom point as percentage
        const percentX = (mouseX + wrapper.scrollLeft) / wrapper.scrollWidth;
        const percentY = (mouseY + wrapper.scrollTop) / wrapper.scrollHeight;

        state.zoom = newZoom;

        // Apply zoom transform
        img.style.transformOrigin = "top left";
        img.style.transform = `scale(${newZoom})`;
        img.style.cursor = newZoom > 1 ? "grab" : "zoom-out";

        // Adjust scroll to zoom toward mouse position
        requestAnimationFrame(() => {
          const newScrollLeft = wrapper.scrollWidth * percentX - mouseX;
          const newScrollTop = wrapper.scrollHeight * percentY - mouseY;
          wrapper.scrollLeft = Math.max(0, newScrollLeft);
          wrapper.scrollTop = Math.max(0, newScrollTop);
        });
      }

      handleMouseDown(e) {
        const img = e.target.closest("img");
        if (!img) return;

        const state = this.zoomState.get(img);
        if (!state || state.zoom <= 1) return;

        const wrapper = img.closest(".product-media-modal__content");
        if (!wrapper) return;

        this.isDragging = true;
        this.dragStart = { x: e.clientX, y: e.clientY };
        this.scrollStart = { x: wrapper.scrollLeft, y: wrapper.scrollTop };
        this.currentWrapper = wrapper;
        this.currentDragImg = img;
        img.style.cursor = "grabbing";
        e.preventDefault();
      }

      handleMouseMove(e) {
        if (!this.isDragging || !this.currentWrapper) return;

        const dx = e.clientX - this.dragStart.x;
        const dy = e.clientY - this.dragStart.y;

        // Mark as actual drag if moved more than 5 pixels
        if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
          this.didDrag = true;
        }

        this.currentWrapper.scrollLeft = this.scrollStart.x - dx;
        this.currentWrapper.scrollTop = this.scrollStart.y - dy;
      }

      handleClick(e) {
        // Prevent click events when dragging occurred
        if (this.didDrag) {
          e.preventDefault();
          e.stopPropagation();
          this.didDrag = false;
        }
      }

      handlePointerUp(e) {
        // Prevent pointerup events when dragging occurred
        // This prevents ModalDialog from closing the modal on drag release
        if (this.didDrag) {
          e.stopPropagation();
          // Don't clear didDrag here - let handleClick do it
        }
      }

      handleMouseUp() {
        if (this.isDragging && this.currentDragImg) {
          const state = this.zoomState.get(this.currentDragImg);
          this.currentDragImg.style.cursor =
            state && state.zoom > 1 ? "grab" : "zoom-out";
        }
        this.isDragging = false;
        this.currentWrapper = null;
        this.currentDragImg = null;
        // Defer clearing didDrag so click event can see it first
        // This also handles mouseleave where no click follows
        setTimeout(() => {
          this.didDrag = false;
        }, 0);
      }

      // Touch handlers for pinch-to-zoom
      handleTouchStart(e) {
        // Find the active image, falling back to event target or first img
        let img = this.findActiveImg() || e.target.closest("img");
        let wrapper =
          img?.closest(".product-media-modal__content") ||
          this.querySelector(".product-media-modal__content");

        if (e.touches.length === 2) {
          // Pinch start
          e.preventDefault();

          // Check if we're continuing a pinch gesture from the gallery
          // Either setupContinuedPinch already ran (initialPinchDistance set) or it hasn't yet (pendingPinchState set)
          // Also use timing-based fallback: if modal opened via pinch within last 500ms, treat as continuation
          const flagBasedContinuation =
            this.isExternalPinchActive &&
            (this.initialPinchDistance !== null ||
              this.pendingPinchState !== null);
          const timingBasedContinuation =
            this.pinchOpenTimestamp &&
            Date.now() - this.pinchOpenTimestamp < 500 &&
            (this.initialPinchDistance !== null ||
              this.pendingPinchState !== null);
          const continuingFromGallery =
            flagBasedContinuation || timingBasedContinuation;

          if (continuingFromGallery) {
            // If setupContinuedPinch hasn't run yet, do it now
            if (this.pendingPinchState && this.initialPinchDistance === null) {
              this.initialPinchDistance =
                this.pendingPinchState.initialPinchDistance;
              this.pinchCenter = this.pendingPinchState.pinchCenter;
              this.initialPinchZoom = 1;
              this.pendingPinchState = null;
            }
            // Update image references if we found them (if not, handleTouchMove will try again)
            if (img) {
              this.currentTouchImg = img;
              this.currentTouchWrapper = wrapper;
              // Initialize zoom state
              if (!this.zoomState.has(img)) {
                this.zoomState.set(img, { zoom: 1, panX: 0, panY: 0 });
              }
            }
            // Now that modal has taken over, clear the external flag
            this.isExternalPinchActive = false;
            // Even if img wasn't found, we've set up the pinch state - handleTouchMove will apply it
            return;
          }

          // For new gestures (not continuing from gallery), we need the image
          if (!img || !wrapper) return;

          // New pinch gesture starting on the modal
          this.initialPinchDistance = this.getTouchDistance(e.touches);
          this.currentTouchImg = img;
          this.currentTouchWrapper = wrapper;

          // Calculate pinch center relative to image (percentage-based for touch)
          const centerX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
          const centerY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
          const imgRect = img.getBoundingClientRect();
          this.pinchCenter = {
            x: ((centerX - imgRect.left) / imgRect.width) * 100,
            y: ((centerY - imgRect.top) / imgRect.height) * 100,
          };

          // Get or initialize zoom state
          let state = this.zoomState.get(img);
          if (!state) {
            state = { zoom: 1, panX: 0, panY: 0 };
            this.zoomState.set(img, state);
          }
          this.initialPinchZoom = state.zoom;
        } else if (e.touches.length === 1 && img && wrapper) {
          // Single touch - check if zoomed for panning
          const state = this.zoomState.get(img);
          if (state && state.zoom > 1) {
            e.preventDefault(); // Prevent page scroll while panning zoomed image
            this.isDragging = true;
            this.dragStart = {
              x: e.touches[0].clientX,
              y: e.touches[0].clientY,
            };
            this.scrollStart = { x: wrapper.scrollLeft, y: wrapper.scrollTop };
            this.currentWrapper = wrapper;
          }
        }
      }

      handleTouchMove(e) {
        if (e.touches.length === 2) {
          // If we have pending pinch state but setup didn't complete, try again
          if (this.pendingPinchState && !this.currentTouchImg) {
            this.setupContinuedPinch();
          }

          // If still no pinch distance, initialize - prefer using pending state for gesture continuity
          if (this.initialPinchDistance === null) {
            const img = this.findActiveImg() || e.target.closest("img");
            if (!img) return;

            const wrapper = img.closest(".product-media-modal__content");
            this.currentTouchImg = img;
            this.currentTouchWrapper = wrapper;

            if (!this.zoomState.has(img)) {
              this.zoomState.set(img, { zoom: 1, panX: 0, panY: 0 });
            }

            if (this.pendingPinchState) {
              // Use the original pinch distance from when gesture started on gallery
              this.initialPinchDistance =
                this.pendingPinchState.initialPinchDistance;
              this.pinchCenter = this.pendingPinchState.pinchCenter;
              this.initialPinchZoom = 1;
              this.pendingPinchState = null;
            } else {
              // No pending state - initialize from current touch (new gesture)
              this.initialPinchDistance = this.getTouchDistance(e.touches);
              this.initialPinchZoom = this.zoomState.get(img).zoom;

              const centerX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
              const centerY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
              const imgRect = img.getBoundingClientRect();
              this.pinchCenter = {
                x: ((centerX - imgRect.left) / imgRect.width) * 100,
                y: ((centerY - imgRect.top) / imgRect.height) * 100,
              };
            }
          }

          // Pinch zoom
          e.preventDefault();
          const currentDistance = this.getTouchDistance(e.touches);
          const scale = currentDistance / this.initialPinchDistance;

          // Calculate the raw (unclamped) zoom to detect zoom-out-to-close gesture
          const rawZoom = this.initialPinchZoom * scale;

          // If zooming out below threshold, close the lightbox
          if (
            rawZoom < this.closeOnZoomOutThreshold &&
            !this.isClosingFromZoomOut
          ) {
            this.isClosingFromZoomOut = true;
            // Reset pinch state before closing
            this.initialPinchDistance = null;
            this.currentTouchImg = null;
            this.currentTouchWrapper = null;
            this.hide();
            return;
          }

          const newZoom = Math.max(1, Math.min(4, rawZoom));

          const img = this.currentTouchImg;
          const wrapper = this.currentTouchWrapper;
          if (!img || !wrapper) return;

          let state = this.zoomState.get(img);
          if (!state) {
            state = { zoom: 1, panX: 0, panY: 0 };
            this.zoomState.set(img, state);
          }

          const isMobile =
            window.matchMedia("(hover: none)").matches ||
            window.matchMedia("(max-width: 749px)").matches;

          if (isMobile) {
            // Mobile: width-based zoom with scroll-based panning
            const centerX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
            const centerY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
            const wrapperRect = wrapper.getBoundingClientRect();
            const relX = centerX - wrapperRect.left;
            const relY = centerY - wrapperRect.top;

            const prevZoom = state.zoom || 1;
            const imgPointX = (relX + wrapper.scrollLeft) / prevZoom;
            const imgPointY = (relY + wrapper.scrollTop) / prevZoom;

            state.zoom = newZoom;
            this.applyZoom(img, newZoom);

            wrapper.scrollLeft = Math.max(0, imgPointX * newZoom - relX);
            wrapper.scrollTop = Math.max(0, imgPointY * newZoom - relY);
          } else {
            // Desktop: CSS transform with percentage-based origin (as original)
            state.zoom = newZoom;
            img.style.transformOrigin = `${this.pinchCenter.x}% ${this.pinchCenter.y}%`;
            img.style.transform = `scale(${newZoom})`;
          }
        } else if (
          e.touches.length === 1 &&
          this.isDragging &&
          this.currentWrapper
        ) {
          // Single touch pan when zoomed
          e.preventDefault(); // Prevent page scroll while panning
          const dx = e.touches[0].clientX - this.dragStart.x;
          const dy = e.touches[0].clientY - this.dragStart.y;

          this.currentWrapper.scrollLeft = this.scrollStart.x - dx;
          this.currentWrapper.scrollTop = this.scrollStart.y - dy;
        }
      }

      handleTouchEnd(e) {
        if (e.touches.length < 2) {
          this.initialPinchDistance = null;
          this.currentTouchImg = null;
          this.currentTouchWrapper = null;
        }
        if (e.touches.length === 0) {
          this.isDragging = false;
          this.currentWrapper = null;
        }
      }

      getTouchDistance(touches) {
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.sqrt(dx * dx + dy * dy);
      }

      // Handle touch events forwarded from media-gallery during pinch-to-open gesture
      handleExternalTouchMove(e) {
        if (e.touches.length < 2) return;

        // If we have pending pinch state but setup didn't complete, try again
        if (this.pendingPinchState && !this.currentTouchImg) {
          this.setupContinuedPinch();
        }

        // Ensure we have the image reference
        if (!this.currentTouchImg) {
          const wrapper = this.querySelector(".product-media-modal__content");
          const img = this.findActiveImg();
          if (img) {
            this.currentTouchImg = img;
            this.currentTouchWrapper =
              wrapper || img.closest(".product-media-modal__content");
            if (!this.zoomState.has(img)) {
              this.zoomState.set(img, { zoom: 1, panX: 0, panY: 0 });
            }
          }
        }

        // If still no pinch distance, initialize - prefer using pending state for gesture continuity
        if (this.initialPinchDistance === null && this.currentTouchImg) {
          if (this.pendingPinchState) {
            // Use the original pinch distance from when gesture started on gallery
            this.initialPinchDistance =
              this.pendingPinchState.initialPinchDistance;
            this.pinchCenter = this.pendingPinchState.pinchCenter;
            this.initialPinchZoom = 1;
            this.pendingPinchState = null;
          } else {
            // No pending state - initialize from current touch (new gesture)
            this.initialPinchDistance = this.getTouchDistance(e.touches);
            this.initialPinchZoom =
              this.zoomState.get(this.currentTouchImg)?.zoom || 1;

            // Calculate pinch center
            const centerX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
            const centerY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
            const imgRect = this.currentTouchImg.getBoundingClientRect();
            this.pinchCenter = {
              x: ((centerX - imgRect.left) / imgRect.width) * 100,
              y: ((centerY - imgRect.top) / imgRect.height) * 100,
            };
          }
        }

        if (!this.currentTouchImg || this.initialPinchDistance === null) return;

        const currentDistance = this.getTouchDistance(e.touches);
        const scale = currentDistance / this.initialPinchDistance;

        // Calculate the raw (unclamped) zoom to detect zoom-out-to-close gesture
        const rawZoom = this.initialPinchZoom * scale;

        // If zooming out below threshold, close the lightbox
        if (
          rawZoom < this.closeOnZoomOutThreshold &&
          !this.isClosingFromZoomOut
        ) {
          this.isClosingFromZoomOut = true;
          this.initialPinchDistance = null;
          this.currentTouchImg = null;
          this.currentTouchWrapper = null;
          this.hide();
          return;
        }

        const newZoom = Math.max(1, Math.min(4, rawZoom));

        const img = this.currentTouchImg;
        const wrapper = this.currentTouchWrapper;
        let state = this.zoomState.get(img);
        if (!state) {
          state = { zoom: 1, panX: 0, panY: 0 };
          this.zoomState.set(img, state);
        }

        const isMobile =
          window.matchMedia("(hover: none)").matches ||
          window.matchMedia("(max-width: 749px)").matches;

        if (isMobile) {
          // Mobile: width-based zoom with scroll-based panning
          const centerX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
          const centerY = (e.touches[0].clientY + e.touches[1].clientY) / 2;

          const prevZoom = state.zoom || 1;
          state.zoom = newZoom;
          this.applyZoom(img, newZoom);

          if (wrapper) {
            const wrapperRect = wrapper.getBoundingClientRect();
            const relX = centerX - wrapperRect.left;
            const relY = centerY - wrapperRect.top;
            const imgPointX = (relX + wrapper.scrollLeft) / prevZoom;
            const imgPointY = (relY + wrapper.scrollTop) / prevZoom;
            wrapper.scrollLeft = Math.max(0, imgPointX * newZoom - relX);
            wrapper.scrollTop = Math.max(0, imgPointY * newZoom - relY);
          }
        } else {
          // Desktop: CSS transform with percentage-based origin (as original)
          state.zoom = newZoom;
          img.style.transformOrigin = `${this.pinchCenter.x}% ${this.pinchCenter.y}%`;
          img.style.transform = `scale(${newZoom})`;
        }
      }

      handleExternalTouchEnd(e) {
        if (e.touches.length < 2) {
          // Keep the zoom state but clear the pinch tracking
          this.initialPinchDistance = null;
        }
        if (e.touches.length === 0) {
          // Gesture completely ended - reset for new gestures inside the modal
          this.currentTouchImg = null;
          this.currentTouchWrapper = null;
          // External pinch gesture ended, allow modal's own handlers to work again
          this.isExternalPinchActive = false;
        }
      }

      // Apply zoom to image using actual width change. This creates correct scrollable
      // area on all platforms (CSS transform + margins causes scroll issues on iOS and
      // incorrect zoom centering on desktop).
      applyZoom(img, zoom) {
        // Store base width on first zoom
        if (!img.dataset.baseWidth) {
          img.dataset.baseWidth = img.offsetWidth;
        }
        const baseWidth = parseFloat(img.dataset.baseWidth);
        // Use actual width change - creates correct scrollable area on all platforms
        img.style.width = `${baseWidth * zoom}px`;
        img.style.maxWidth = "none";
        img.style.transform = "";
        img.style.transformOrigin = "";
        img.style.marginRight = "";
        img.style.marginBottom = "";
      }

      resetAllZoom() {
        this.zoomState.forEach((state, img) => {
          img.style.transform = "";
          img.style.transformOrigin = "";
          img.style.cursor = "";
          img.style.marginRight = "";
          img.style.marginBottom = "";
          img.style.width = "";
          img.style.maxWidth = "";
          delete img.dataset.baseWidth;
        });
        this.zoomState.clear();
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute("data-media-id")}"])`,
        ).forEach((element) => {
          element.classList.remove("active");
        });
        const activeMedia = this.querySelector(
          `[data-media-id="${this.openedBy.getAttribute("data-media-id")}"]`,
        );
        const activeMediaTemplate = activeMedia.querySelector("template");
        const activeMediaContent = activeMediaTemplate
          ? activeMediaTemplate.content
          : null;
        activeMedia.classList.add("active");

        // When click position is available, applyInitialZoom will handle scrolling
        // Otherwise, scroll to the image after a short delay
        if (!this.clickPosition) {
          setTimeout(() => {
            activeMedia.scrollIntoView({ behavior: "instant", block: "start" });
          }, 50);
        }

        if (
          activeMedia.nodeName == "DEFERRED-MEDIA" &&
          activeMediaContent &&
          (activeMediaContent.querySelector(".js-youtube") ||
            activeMediaContent.querySelector("video"))
        )
          activeMedia.loadContent();
      }
    },
  );
}
